import { PhxDatePickerService } from './../date-picker.service';

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { FocusKeyManager } from '@angular/cdk/a11y';

import { IDatesTooltip, IDateWithMeta, IFilledMonth, IPhxDateEvent, ISelectedRange, Status } from '../utils/types';
import { DaysName } from '../utils/constants';
import { PhxFocusableItemDirective } from '../phx-focusable-item.directive';
import { KeyboardKeys } from '../../../helpers/KeyboardKeys.enum';
import { DotFilled } from './../../icon/icon-map';
import { PhxTranslatePipe } from '../../../translation/phx-translation.pipe';
import { PhxTooltipPlacement } from '../../tooltip/phx-tooltip.types';
import { phxWebStyleTokens } from '../../../styles/phx-tokens';
import { startOfDay } from 'date-fns';

const EVENT_COLORS = [
  phxWebStyleTokens.colorBackgroundBoldAccent,
  phxWebStyleTokens.colorBackgroundBoldNeutral,
  phxWebStyleTokens.colorContentNegative,
];

@Component({
  selector: 'phx-date-picker-body',
  host: {
    class: 'phx-date-picker-body',
    '[class.phx-date-range-picker-body]': 'phxIsRangeEnabled',
    '[class.phx-date-picker-body-has-events]': 'phxEvents?.length > 0',
    '[tabIndex]': '0',
    '(focus)': 'onWrapperFocus()',
  },
  templateUrl: './body.component.html',
  styleUrls: ['./body.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhxDatePickerBodyComponent implements OnInit, OnChanges {
  public readonly eventColor: string = phxWebStyleTokens.colorBackgroundBoldAccent;
  public readonly eventIconSize: number = 4;
  public daysName: string[] = this.phxDatePickerService.allDaysOfWeek();
  public allDaysOfMonth: Record<number, IDateWithMeta>;
  public hoveredDateRangeArray: number[];
  public DotFilledIcon: string = DotFilled;
  public tooltipText: string = null;
  public tooltipPlacement: PhxTooltipPlacement = 'top';
  public eventColorMap = new Map<string, string>();
  private _selectedDateRange: ISelectedRange;
  private _pastSelectableDate: Date;
  private _futureSelectableDate: Date;
  private _dateEventsMap = new Map<number, string[]>();
  private _enableDatesSet: Set<number>;

  @Input() public set selectedDateRange(selectedRange: ISelectedRange) {
    this._selectedDateRange = this.phxDatePickerService.transformRangeToStartOfDayRange(selectedRange);
  }

  public get selectedDateRange(): ISelectedRange {
    return this._selectedDateRange;
  }

  @Input() public date: Date;
  @Input() public phxIsFullWeekSelectionEnabled: boolean = false;
  @Input() public phxIsRangeEnabled: boolean;
  @Input() public phxMinSelectableDate: Date;
  @Input() public phxMaxSelectableDate: Date;
  @Input() public phxSelectableRangeLimit: number;
  @Input() public phxEvents: IPhxDateEvent[];
  @Input() public phxDatesTooltip: IDatesTooltip[];
  @Input() public phxEnabledDates: Date[];

  @Output() public readonly onDateSelection = new EventEmitter<ISelectedRange>();

  @ViewChildren(PhxFocusableItemDirective)
  private focusableItems: QueryList<PhxFocusableItemDirective>;
  private keyManager: FocusKeyManager<PhxFocusableItemDirective>;


  constructor(
    private phxDatePickerService: PhxDatePickerService,
    private _phxTranslate: PhxTranslatePipe,
  ) {}

  public onWrapperFocus(): void {
    this.keyManager.setFirstItemActive();
  }

  public onKeyup(date: IDateWithMeta, event: KeyboardEvent): void {
    switch (event.code) {
      case KeyboardKeys.ARROW_LEFT:
      case KeyboardKeys.ARROW_UP:
        this.phxIsRangeEnabled && this.onDateHover(date.date);
        break;
      case KeyboardKeys.ARROW_RIGHT:
      case KeyboardKeys.ARROW_DOWN:
        this.phxIsRangeEnabled && this.onDateHover(date.date);
        break;
    }
  }

  public getEventColorByName(eventName: string): string {
    return this.eventColorMap.get(eventName);
  }

  public onKeydown(date: IDateWithMeta, event: KeyboardEvent): void {
    switch (event.code) {
      case KeyboardKeys.ARROW_LEFT:
      case KeyboardKeys.ARROW_UP:
        event.preventDefault();
        this.keyManager.setPreviousItemActive();
        break;
      case KeyboardKeys.ARROW_RIGHT:
      case KeyboardKeys.ARROW_DOWN:
        event.preventDefault();
        this.keyManager.setNextItemActive();
        break;
      case KeyboardKeys.ENTER:
      case KeyboardKeys.SPACE:
        event.preventDefault();
        this.dateSelected(date.date);
        break;
    }
  }

  ngAfterViewInit(): void {
    this.focusableItems.changes.subscribe(() => {
      this.keyManager = new FocusKeyManager(this.focusableItems).skipPredicate((item: PhxFocusableItemDirective) => item.skip).withWrap();
    });
    this.focusableItems.notifyOnChanges();
  }

  ngOnInit(): void {
    this.initateCalendar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { date: changedDate, selectedDateRange: changedSelectedDate, phxEvents: changedPhxEvents, phxEnabledDates: changedPhxEnabledDates } = changes;

    // Convert the phxEnabledDates to a Set for faster lookup
    if (changedPhxEnabledDates?.currentValue && changedPhxEnabledDates?.currentValue.length && changedPhxEnabledDates?.currentValue !== changedPhxEnabledDates?.previousValue) {
      this._enableDatesSet = new Set(changedPhxEnabledDates.currentValue.map((date: Date) => this.getUniqueTimeKey(startOfDay(date))));
    }

    // If the `date` property changed, re-initialize the calendar
    if (changedDate) this.initateCalendar();

    // If the `selectedDateRange` property changed
    if (changedSelectedDate?.currentValue !== changedSelectedDate?.previousValue) {
      const prevSelection: ISelectedRange = this.phxDatePickerService.transformRangeToStartOfDayRange(changedSelectedDate.previousValue);
      // before selection clearing all selection
      if (prevSelection?.end) {
        this.hoveredDateRangeArray = [];
        this.setRangeSelectionAndUpdateStatus(prevSelection, false);
      } else if (prevSelection?.start) {
        this.setSelectionAndUpdateStatus(prevSelection.start, false);
      }

      // Select the new range and update the status
      if (this.selectedDateRange?.end) {
        this.hoveredDateRangeArray = [];
        this.resetSelectableRange();
        this.setRangeSelectionAndUpdateStatus(this.selectedDateRange, true);
      } else if (this.selectedDateRange?.start) {
        this.setSelectionAndUpdateStatus(this.selectedDateRange.start, true);
      } else {
        // hover selection clear
        this.hoveredDateRangeArray = [];
        this.resetSelectableRange();
      }
    }

    // If the `phxEvents` property changed, update the event color map
    if (changedPhxEvents && changedPhxEvents.currentValue) {
      this.phxEventChanged(changedPhxEvents.currentValue);
    }
  }

  private phxEventChanged(phxEvents: IPhxDateEvent[]): void {
    if (phxEvents.length > 3) console.warn("Phx-date only support three events a max");
    this._dateEventsMap.clear();
    this.eventColorMap.clear();
    phxEvents.forEach((event, index) => {
      this.eventColorMap.set(event.name, EVENT_COLORS[index % EVENT_COLORS.length]);
      event.dates.forEach((date) => {
        const key = this.getUniqueTimeKey(startOfDay(date));
        const existingEvents = this._dateEventsMap.get(key);
        if (existingEvents) {
          existingEvents.push(event.name);
        } else {
          this._dateEventsMap.set(key, [event.name]);
        }
      });
    });
    this.initateCalendar();
  }

  /**
   * Initializes the calendar with the current month and sets metadata for each day.
   *
   * @returns {void}
   */
  private initateCalendar(eventOndates: Date[] = []): void {
    if (!this.date) this.date = new Date();

    let selectedDates: Date[] = [];
    if (this.selectedDateRange?.start) {
      if (this.phxIsRangeEnabled && this.selectedDateRange?.end) {
        selectedDates = this.phxDatePickerService.getDatesInRange(this.selectedDateRange.start, this.selectedDateRange.end);
      } else {
        selectedDates = [this.selectedDateRange.start];
      }
    }

    // Get an object containing arrays for the previous, current, and next months
    let filledMonth: IFilledMonth = this.phxDatePickerService.getAllDayOfMonth(this.date);
    this.allDaysOfMonth = {};
    // Add metadata to each day of the month
    this.addMetaData(filledMonth.prevMonth, false, selectedDates, eventOndates);
    this.addMetaData(filledMonth.currMonth, true, selectedDates, eventOndates);
    this.addMetaData(filledMonth.nextMonth, false, selectedDates, eventOndates);
  }

  /**
   * Handles the selection of a date in the calendar.
   *
   * @param {IDateWithMeta} date The selected date object.
   * @returns {void}
   */
  public dateSelected(date: Date): void {
    // for fixed range selection
    if (!this.phxSelectableRangeLimit && this.phxIsFullWeekSelectionEnabled && this.hoveredDateRangeArray.length) {
      this.selectedDateRange = this.getFixedRange(date);
    } else if (this.phxIsRangeEnabled) {
      // nothing selected or 'from' or 'to' selected
      if (!this.selectedDateRange || !Object.keys(this.selectedDateRange).length || this.selectedDateRange?.end) {
        this.selectedDateRange = { start: date };
        // For disabling dates before and after selectable range
        if (this.phxSelectableRangeLimit) {
          this._pastSelectableDate = this.phxDatePickerService.subNoOfDays(date, this.phxSelectableRangeLimit);
          this._futureSelectableDate = this.phxDatePickerService.addNoOfDays(date, this.phxSelectableRangeLimit);
          const tooltipText = `${this._phxTranslate.transform('Select up to')} ${this.phxSelectableRangeLimit} ${this._phxTranslate.transform('days')}`;
          this.updateDisableState(tooltipText);
        }
      } else {
        // 'from' selected already
        this.hoveredDateRangeArray = [];
        if (this.phxDatePickerService.isBeforeDate(date, this.selectedDateRange?.start)) {
          const { start: end } = this.selectedDateRange;
          this.selectedDateRange = { start: date, end };
        } else {
          this.selectedDateRange = {
            ...this.selectedDateRange,
            end: date,
          };
        }
        // For reanabling dates after selecting end date
        this.resetSelectableRange();
      }
    } else {
      this.selectedDateRange = { start: date };
    }
    const selectedRange: ISelectedRange = {
      start: this.selectedDateRange?.start,
      ...(this.phxIsRangeEnabled &&
        this.selectedDateRange?.end && {
        end: this.selectedDateRange?.end,
      }),
    };
    this.onDateSelection.emit(selectedRange);
  }

  /**
   * Resolves the status of a date based on its metadata.
   *
   * @param {IDateWithMeta} date The date object with metadata.
   * @returns {string} The status string for the given date.
   */
  public initialStatus(date: IDateWithMeta): string {
    if (date.isDisabled) {
      if (date.isToday && date.isSelected) return Status.INACTIVE_TODAY_SELECTED;
      if (date.isSelected) return Status.INACTIVE_SELECTED;
      if (date.isToday) return Status.INACTIVE_TODAY;
      return Status.INACTIVE;
    } else if (!date.isCurrentMonth) {
      if (date.isToday && date.isSelected && date.hasEvent) return Status.PREV_NEXT_MONTH_TODAY_SELECTED_HAS_EVENT;
      if (date.isToday && date.isSelected) return Status.PREV_NEXT_MONTH_TODAY_SELECTED;
      if (date.isSelected && date.hasEvent) return Status.PREV_NEXT_MONTH_SELECTED_HAS_EVENT;
      if (date.isToday && date.hasEvent) return Status.PREV_NEXT_MONTH_TODAY_HAS_EVENT;
      if (date.isToday) return Status.PREV_NEXT_MONTH_TODAY;
      if (date.isSelected) return Status.PREV_NEXT_MONTH_SELECTED;
      if (date.hasEvent) return Status.PREV_NEXT_MONTH_HAS_EVENT;
      return Status.PREV_NEXT_MONTH;
    } else {
      if (date.isToday && date.isSelected && date.hasEvent) return Status.ACTIVE_TODAY_SELECTED_HAS_EVENT;
      if (date.isToday && date.isSelected) return Status.ACTIVE_TODAY_SELECTED;
      if (date.isSelected && date.hasEvent) return Status.ACTIVE_SELECTED_HAS_EVENT;
      if (date.isToday && date.hasEvent) return Status.ACTIVE_TODAY_HAS_EVENT;
      if (date.isToday) return Status.ACTIVE_TODAY;
      if (date.isSelected) return Status.ACTIVE_SELECTED;
      if (date.hasEvent) return Status.ACTIVE_HAS_EVENT;
      return Status.ACTIVE;
    }
  }

  /**
   * Resolves the appropriate CSS class for a date cell in the date picker based on the hovered or selected date range.
   *
   * @param {number} dateKey - The unique identifier for the date cell being rendered.
   * @returns {string} The CSS class to be applied to the date cell.
   */
  public resolveHoverClass(dateKey: number | string): string {
    if (typeof dateKey === 'string') dateKey = parseInt(dateKey);
    const dateWithMeta = this.allDaysOfMonth[dateKey];
    const hoveredArrLastIdx: number = this.hoveredDateRangeArray?.length - 1;
    if (!this.phxSelectableRangeLimit && this.phxIsFullWeekSelectionEnabled && this.isInHoveredRange(dateKey)) {
      // If the fixed range feature is enabled and the date is within the hovered range, determine the appropriate CSS class based on whether the date is selected, the start of the range, or the end of the range.
      if (dateWithMeta?.isSelected) return '';
      if (+dateKey === this.hoveredDateRangeArray[0]) {
        return 'phx-date-picker-body-date-cell-hover-from';
      } else if (+dateKey === this.hoveredDateRangeArray[hoveredArrLastIdx]) {
        return 'phx-date-picker-body-date-cell-hover-to';
      } else {
        return 'phx-date-picker-body-date-cell-hover';
      }
    } else if (this.selectedDateRange?.start && this.isInHoveredRange(dateKey) && hoveredArrLastIdx > 0) {
      // If a date range has been selected and the date is within the hovered range,
      // determine the appropriate CSS class based on whether the date is the start of the range,
      // the end of the range, or somewhere in between.
      if (+dateKey === this.hoveredDateRangeArray[0]) {
        return `phx-date-picker-body-date-cell-hover-from${dateWithMeta?.isSelected ? '-selected' : ''}`;
      } else if (+dateKey === this.hoveredDateRangeArray[hoveredArrLastIdx]) {
        return `phx-date-picker-body-date-cell-hover-to${dateWithMeta?.isSelected ? '-selected' : ''}`;
      }
      return 'phx-date-picker-body-date-cell-hover';
    }
    // If none of the conditions above are met, return an empty string.
    return '';
  }

  /**
   * Handles the logic for when a user hovers over a date in the date picker.
   *
   * @param {Date} date - The date that the user is hovering over.
   * @returns {void}
   */
  public onDateHover(date: Date): void {
    if (this.phxIsRangeEnabled) {
      if (!this.phxSelectableRangeLimit && this.phxIsFullWeekSelectionEnabled) {
        // If the fixed range feature is enabled, get the dates within the fixed hover range.
        this.hoveredDateRangeArray = this.getFixedHoverRangeDates(date);
      } else if (this.selectedDateRange?.start && this.selectedDateRange?.end) {
        // If a date range has already been selected, clear the hovered date range.
        this.hoveredDateRangeArray = [];
      } else if (this.selectedDateRange?.start) {
        this.setTooltipPlacement(date, this.selectedDateRange.start);
        // If a start date has been selected, calculate the hovered date range based on the start date and the date being hovered over.
        let dates: Date[] = [];
        if (this.phxDatePickerService.isBeforeDate(date, this.selectedDateRange?.start)) {
          dates = this.phxDatePickerService.getDatesInRange(date, this.selectedDateRange.start);
        } else {
          dates = this.phxDatePickerService.getDatesInRange(this.selectedDateRange.start, date);
        }
        // Convert the dates to their respective timestamps and store them in the hovered date range array.
        this.hoveredDateRangeArray = dates.map((date) => date.getTime());
      }
    }
  }

  public isInHoveredRange(dateUniquekey: number): boolean {
    return this.hoveredDateRangeArray ? this.hoveredDateRangeArray.includes(+dateUniquekey) : false;
  }

  private setTooltipPlacement(hoveredDate: Date, startDate: Date): void {
    if (this.phxDatePickerService.isBeforeDate(hoveredDate, startDate)) {
      this.tooltipPlacement = 'top';
    } else {
      this.tooltipPlacement = 'bottom';
    }
  }

  /**
   * Adds metadata to each date in an array of dates
   *
   * @param {Date[]} dates - An array of dates to add metadata to
   * @param {boolean} currentMonth - A flag indicating if the dates are in the current month
   * @param {Date[]} [selectedDates=[]] - An optional array of selected dates
   * @param {Date[]} [eventOndates=[]] - An optional array of dates with events
   * @returns {void}
   */
  private addMetaData(dates: Date[], currentMonth: boolean, selectedDates: Date[] = [], eventOndates: Date[] = []): void {
    dates.map((date) => {
      let currentDate: boolean = this.phxDatePickerService.isTodayDate(date);
      let isSelected: boolean = selectedDates.some((d) => this.phxDatePickerService.isSameDate(d, date));
      let hasEvent: boolean = eventOndates.some((d) => this.phxDatePickerService.isSameDate(d, date));
      const tooltipText = this.getTooltipForDates(date);
      const key: number = this.getUniqueTimeKey(date);
      let events: string[] = this._dateEventsMap.get(key);
      let isDisabled: boolean = this.shouldDisableDate(date);
      const dayLabel = date.getDate().toString();
      const dateInitialState: IDateWithMeta = {
        isDisabled,
        status: currentMonth ? Status.ACTIVE : Status.PREV_NEXT_MONTH,
        day: dayLabel,
        date: date,
        isToday: currentDate,
        isCurrentMonth: currentMonth,
        isSelected,
        hasEvent,
        events,
        tooltipText,
      };

      const dateWithMeta: IDateWithMeta = {
        isDisabled,
        day: dayLabel,
        date: date,
        status: this.resolveStatus(dateInitialState),
        isToday: currentDate,
        isCurrentMonth: currentMonth,
        isSelected,
        hasEvent,
        events,
        tooltipText,
      };
      this.allDaysOfMonth[this.getUniqueTimeKey(dateWithMeta.date)] = dateWithMeta;
    });
  }

  private getTooltipForDates(date: Date): string {
    let tooltipText: string = null;
    if (this.phxDatesTooltip && this.phxDatesTooltip.length) {
      this.phxDatesTooltip.forEach(tooltipLabel => {
        if (tooltipLabel.dateRange && this.phxDatePickerService.isDateInRange(date, tooltipLabel.dateRange)) {
          tooltipText = tooltipLabel.label;
        } else if (tooltipLabel.dates && tooltipLabel.dates.some(d => this.phxDatePickerService.isSameDate(d, date))) {
          tooltipText = tooltipLabel.label;
        }
      });
    }
    return tooltipText;
  }
  /**
   * Sets the selection state of a date and updates its status in the metadata object.
   * @param date The date to set the selection state and update the status for.
   * @param isSelected The selection state to set for the date.
   * @returns Void.
   */
  private setSelectionAndUpdateStatus(date: Date, isSelected: boolean): void {
    // Retrieve the metadata object for the date.
    let dateWithMeta = this.allDaysOfMonth[this.getUniqueTimeKey(date)];

    // If metadata object exists, update its properties.
    if (dateWithMeta) {
      dateWithMeta.isSelected = isSelected;
      dateWithMeta.status = this.resolveStatus(dateWithMeta);

      // Update the metadata object in the allDaysOfMonth object.
      this.allDaysOfMonth[this.getUniqueTimeKey(dateWithMeta.date)] = dateWithMeta;
    }
  }

  /**
   * Sets the selection state and updates the metadata of all dates in the range specified by the `dateRangePointer` object.
   * @param dateRangePointer An object that specifies the start and end dates of the range to update.
   * @param isSelected The selection state to set for all dates in the range.
   * @returns Void.
   */
  private setRangeSelectionAndUpdateStatus(dateRangePointer: ISelectedRange, isSelected: boolean): void {
    // Get an array of dates within the specified range.
    const dates = this.phxDatePickerService.getDatesInRange(dateRangePointer.start, dateRangePointer.end);

    // For each date in the array, set the selection state and update the metadata.
    dates.forEach((date) => {
      this.setSelectionAndUpdateStatus(date, isSelected);
    });
  }

  /**
   * Resolves the status of the date cell.
   * If range selection is disabled, simply returns the status for a single date selection.
   * If range selection is enabled, returns the status for multi-date selection based on the selectedDateRange.
   *
   * @private
   * @param {IDateWithMeta} selectedDateWithMeta The selected date and its meta information
   * @returns {string} The resolved status of the date cell
   */
  private resolveStatus(selectedDateWithMeta: IDateWithMeta): string {
    if (!this.phxIsRangeEnabled) {
      // simply return the status for a single date selection
      return this.initialStatus(selectedDateWithMeta);
    } else {
      // return the status for multi-date selection based on the selectedDateRange
      return this.phxDatePickerService.transformToMultiSelection(this.initialStatus(selectedDateWithMeta), selectedDateWithMeta, this.selectedDateRange);
    }
  }

  private getUniqueTimeKey(date: Date): number {
    return date.getTime();
  }

  private getFixedHoverRangeDates(date: Date, startDay: DaysName = DaysName.MONDAY): number[] {
    const selectedRange = this.getFixedRange(date, startDay);
    return this.phxDatePickerService.getDatesInRange(selectedRange.start, selectedRange.end).map((date) => date.getTime());
  }

  private getFixedRange(date: Date, startDay: DaysName = DaysName.MONDAY): Required<ISelectedRange> {
    return {
      start: this.phxDatePickerService.startOfWeekDate(date, startDay),
      end: this.phxDatePickerService.endOfWeekDate(date, startDay),
    };
  }

  /**
   * Check whether the given date should be disable or not based on past and future restrictions
   * @param date {Date} - Date which needs to be disable
   * @returns {boolean}
   */
  private shouldDisableDate(date: Date): boolean {
    if (this.phxSelectableRangeLimit) {
      return (
        this.phxDatePickerService.isBeforeDate(date, this.phxMinSelectableDate) ||
        this.phxDatePickerService.isAfterDate(date, this.phxMaxSelectableDate) ||
        this.phxDatePickerService.isSameAndBefore(date, this._pastSelectableDate) ||
        this.phxDatePickerService.isSameAndAfter(date, this._futureSelectableDate)
      );
    }

    if(this.phxEnabledDates && this.phxEnabledDates.length && !this.phxIsRangeEnabled) {
      const isDateEnabled = this._enableDatesSet.has(this.getUniqueTimeKey(startOfDay(date)));
      return !isDateEnabled;
    }

    return this.phxDatePickerService.isBeforeDate(date, this.phxMinSelectableDate) || this.phxDatePickerService.isAfterDate(date, this.phxMaxSelectableDate);
  }

  /**
   * Resets selectable range restrictions
   */
  private resetSelectableRange(): void {
    if (this._pastSelectableDate && this._futureSelectableDate) {
      this._pastSelectableDate = null;
      this._futureSelectableDate = null;
      this.updateDisableState();
    }
  }

  /**
   * Update disable state of the individual date cells based on the values of past and future restrictions
   */
  private updateDisableState(tooltipText?: string): void {
    Object.values(this.allDaysOfMonth).forEach((dateWithMeta) => {
      let dateTooltip = this.getTooltipForDates(dateWithMeta.date);
      dateWithMeta.isDisabled = this.shouldDisableDate(dateWithMeta.date);
      if (tooltipText && dateWithMeta.isDisabled) {
        dateWithMeta.tooltipText = tooltipText;
      } else {
        dateWithMeta.tooltipText = dateTooltip;
      }
      dateWithMeta.status = this.resolveStatus(dateWithMeta);
    });
  }

}
